import Effects from 'Effects';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import Tone from 'tone';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {AbstractAudioParamParameter,
        AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';
import {decibelsToLinear, linearToDecibels} from 'utils/db.js';

const SIZE = 65536;
let curve = new Float32Array(SIZE);

class Saturator extends Effects.Effect {

    constructor(sequencerEngine) {
        super('Saturator', () => new Tone.Gain(1), sequencerEngine); 

        this.waveShaperNode = new Tone.WaveShaper([-1, 1]);
        this.effectNode.connect(this.waveShaperNode);
        this.postGain = new Tone.Gain(1);
        this.waveShaperNode.connect(this.postGain);

        this.params = this.getParams(-10, 20, 30, 6);

        this.driveParameter = new AbstractAudioParamParameter(
            'Drive', this.effectNode.gain, (obj, drive) => {
                this.effectNode.gain.value = drive;
                this.postGain.gain.value = Math.pow(1 / drive, 0.6);
            },
            {
                documention: "Controls the level of saturation.",
                min: 0.01,
                max: 32,
                unit: '',
                defaultValue: 1,
                constantAutomationRate: true
            });
        this.registerParameter(this.driveParameter);

        this.waveShaperNode.curve = this.generateCurve();
        this.updateInitialValues();
    }

    get documentation() {
        return "Saturates the sound, making it more distorted.";
    }

    dispose() {
        this.driveParameter.dispose(false);
        super.dispose();

        this.postGain.dispose();
        this.waveShaperNode.dispose();

        this.postGain = undefined;
        this.waveShaperNode = undefined;
        this.driveParameter = undefined;
    }

    connect(node) {
        this.postGain.connect(node);
    }

    disconnect(node) {
        this.postGain.disconnect(node);
    }

    getParams(dbThreshold, dbKnee, ratio, makeupGain) {
        // Update curve state.
        var linearThreshold = decibelsToLinear(dbThreshold);
        var linearKnee = decibelsToLinear(dbKnee);
        
        
        // Makeup gain.
        var maximum = 1.05 * linearKnee * linearThreshold;
        
        // Compute knee threshold.
        let slope = 1 / ratio;
        
        let k = this.kAtSlope(1 / ratio, dbThreshold, dbKnee, linearThreshold);
        // console.log("k = " + k);
        
        let kneeThresholdDb = dbThreshold + dbKnee;
        let kneeThreshold = decibelsToLinear(kneeThresholdDb);
        
        let ykneeThresholdDb = linearToDecibels(this.saturateBasic(
            kneeThreshold, k, linearThreshold));

        return {
            linearThreshold,
            thresholdDb : dbThreshold,
            kneeDb : dbKnee,
            ykneeThresholdDb,
            kneeThreshold,
            kneeThresholdDb,
            k,
            ratio,
            slope
        };
    }

    // Approximate 1st derivative with input and output expressed in dB.
    // This slope is equal to the inverse of the compression "ratio".
    // In other words, a compression ratio of 20 would be a slope of 1/20.
    slopeAt(x, k, linearThreshold=this.params.linearThreshold) {
        if (x < linearThreshold)
            return 1;
        
        var x2 = x * 1.001;
        
        var xDb = linearToDecibels(x);
        var x2Db = linearToDecibels(x2);
        
        var yDb = linearToDecibels(this.saturateBasic(x, k, linearThreshold));
        var y2Db = linearToDecibels(this.saturateBasic(x2, k, linearThreshold));
        
        var m = (y2Db - yDb) / (x2Db - xDb);
        
        return m;
    }
    
    kAtSlope(
        slope,
        thresholdDb=this.params.thresholdDb,
        kneeDb=this.params.kneeDb,
        linearThreshold=this.params.linearThreshold)
    {
        var xDb = thresholdDb + kneeDb;
        var x = decibelsToLinear(xDb);
        
        var minK = 0.1;
        var maxK = 10000;
        var k = 5;
        
        // Approximate.
        for (var i = 0; i < 15; ++i) {
            var m = this.slopeAt(x, k, linearThreshold);
            
            // console.log("slope = " + slope + " : m = " + m + " : k = " + k);
            
            if (m < slope) {
                // k is too high.
                maxK = k;
                k = Math.sqrt(minK * maxK);
            } else {
                // k is not high enough.
                minK = k;
                k = Math.sqrt(minK * maxK);
            }
        }
        
        return k;
    }
    
    // Exponential saturation curve.
    saturateBasic(x, k, linearThreshold=this.params.linearThreshold) {
        if (x < linearThreshold)
            return x;
        
        return linearThreshold + (1 - Math.exp(-k * (x  - linearThreshold))) / k;
    }
    
    saturate(x) {
        var y;
        
        if (x < this.params.kneeThreshold) {
            y = this.saturateBasic(x, this.params.k);
        } else {
            var xDb = linearToDecibels(x);
            var yDb = this.params.ykneeThresholdDb + this.params.slope * (xDb - this.params.kneeThresholdDb);
            
            y = decibelsToLinear(yDb);
        }
        
        return y;
    }

    generateCurve() {
        var n = SIZE;
        var n2 = n / 2;
        
        var x = 0;
        for (var i = 0; i < n2; ++i) {
            x = i / n2;
            x = this.saturate(x);
            
            curve[n2 + i] = x;
            curve[n2 - i - 1] = -x;
        }

        return curve;
    }


    getJson() {
        let json = {
            ... super.getJson(),
            drive: this.driveParameter.get()
        };

        json = this.saveParameterToJson(json, 'driveParameter', this.driveParameter);
        return json;
    }

    fromJson(json, isCopy) {
        let bulk = super.fromJson(json, isCopy);
        bulkUpdate(this.driveParameter, json.drive, bulk);
        return {
            ... bulk,
            ... this.loadParameterFromJson(json, 'driveParameter', this.driveParameter, isCopy)
        };
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        var copy = new Saturator(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }
}

export default Saturator;
